import React, { useCallback } from 'react';
import { cn } from '../../utils/cn';
import { useAssetPreviewStore, AssetPreviewInstance } from '../../store/assetPreviewStore';

interface AssetPreviewHeaderProps {
  instance: AssetPreviewInstance;
  onStartDrag: (e: React.MouseEvent) => void;
}

export const AssetPreviewHeader: React.FC<AssetPreviewHeaderProps> = ({
  instance,
  onStartDrag,
}) => {
  const { closePreview, minimizePreview } = useAssetPreviewStore();

  const handleClose = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      closePreview(instance.id);
    },
    [closePreview, instance.id]
  );

  const handleMinimize = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      minimizePreview(instance.id);
    },
    [minimizePreview, instance.id]
  );

  return (
    <div
      className={cn(
        'flex items-center h-8 bg-zinc-800 border-b border-zinc-700',
        'cursor-move select-none'
      )}
      onMouseDown={onStartDrag}
    >
      {/* Mac-style controls - LEFT side */}
      <div className="flex items-center gap-1.5 px-2">
        {/* Close (red) */}
        <button
          className={cn(
            'w-3 h-3 rounded-full bg-red-500',
            'hover:bg-red-400 active:bg-red-600',
            'transition-colors',
            'flex items-center justify-center',
            'group'
          )}
          onClick={handleClose}
          title="Close"
        >
          <svg
            className="w-2 h-2 text-red-900 opacity-0 group-hover:opacity-100 transition-opacity"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M2 2L8 8M8 2L2 8" />
          </svg>
        </button>

        {/* Minimize (yellow) */}
        <button
          className={cn(
            'w-3 h-3 rounded-full bg-yellow-500',
            'hover:bg-yellow-400 active:bg-yellow-600',
            'transition-colors',
            'flex items-center justify-center',
            'group'
          )}
          onClick={handleMinimize}
          title="Minimize"
        >
          <svg
            className="w-2 h-2 text-yellow-900 opacity-0 group-hover:opacity-100 transition-opacity"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M2 5H8" />
          </svg>
        </button>

        {/* NO GREEN DOCK BUTTON - intentionally omitted */}
      </div>

      {/* Asset name + dirty indicator */}
      <div className="flex items-center gap-1 flex-1 min-w-0 px-2">
        <span className="text-sm text-zinc-300 truncate">{instance.assetName}</span>
        {instance.isDirty && (
          <span className="text-yellow-500 text-sm" title="Unsaved changes">
            *
          </span>
        )}
      </div>

      {/* Asset type badge */}
      <div className="px-2">
        <span className="text-xs text-zinc-500 uppercase">{instance.assetType}</span>
      </div>
    </div>
  );
};
